import {
	Box,
	Typography,
	IconButton,
	Card,
	CardHeader,
	CardContent,
	CircularProgress,
	Skeleton,
} from '@mui/material';
import { EasTooltip } from 'ui/EasTooltip';
import { TEmManagementByIdFullResponse, TEmSites } from 'redux/reducers/emManagement/types';
import { EEmSitePlatform, EmSitePlatformImages } from 'redux/reducers/emManagement/constants';
import { CheckCircle, ContentCopy, Edit, Help, ShoppingBag } from '@mui/icons-material';
import { HelpButton } from 'ui/HelpButton';
import { useState } from 'react';
import { toast } from 'react-toastify';
import { EmSitesManageDialog as EmSitesManageDialogComponent } from './EmSitesManageDialog';
import { COLORS } from '../../../../services/constants/colors';
import { EHelpButtonArticles } from '../../../../ui/HelpButton/constants';
import { nanoid } from '@reduxjs/toolkit';

export const EmSites = ({
	emDataFull,
	load,
}: {
	emDataFull: TEmManagementByIdFullResponse;
	load: boolean;
}) => {
	const [dialogOpen, setDialogOpen] = useState(false);
	const [dialogMode, setDialogMode] = useState<'list' | 'create'>('list');
	const [isCopied, setIsCopied] = useState(false);
	const [copyUrl, setCopyUrl] = useState<string | null>(null);
	return (
		<Card
			sx={{
				'&': {
					borderRadius: '12px',
					padding: '0px!important',
				},
				m: 1,
				p: 0,
			}}
		>
			<CardHeader
				sx={{
					backgroundColor: COLORS.BACKGROUND_SECONDARY,
					p: 0.5,
					m: 0,
				}}
				title={
					<Box
						sx={{
							display: 'flex',
							flexDirection: 'row',
							justifyContent: 'space-between',
							alignItems: 'center',
							p: 0,
							m: 0,
						}}
					>
						<Typography
							sx={{
								fontSize: '1rem',
								color: 'primary.main',
								fontWeight: 'bold',
								p: 0,
								m: 0,
								ml: 0.5,
							}}
						>
							Sites
						</Typography>
						<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, p: 0, m: 0 }}>
							{/* <IconButton
						size="small"
						color="primary"
						onClick={() => {
							setDialogMode('create');
							setDialogOpen(true);
						}}
					>
						<AddCircle />
					</IconButton> */}
							{load && <CircularProgress size={20} />}
							<IconButton
								size="small"
								color="primary"
								onClick={() => {
									setDialogMode('list');
									setDialogOpen(true);
								}}
								sx={{ p: 0, m: 0 }}
							>
								<Edit sx={{ fontSize: '1.5rem', p: 0, m: 0 }} />
							</IconButton>
							<HelpButton type={EHelpButtonArticles.CUSTOMER_STORES} />
						</Box>
					</Box>
				}
			/>
			<CardContent sx={{ p: 0, m: 0, minHeight: '20px', pb: '0px!important' }}>
				{!emDataFull?.sites?.length && !load && (
					<Typography
						sx={{
							p: 1,
							m: 0,
							display: 'flex',
							justifyContent: 'center',
							alignItems: 'center',
							height: '50px',
							color: COLORS.SECONDARY,
							fontSize: '24px',
							fontWeight: 'bold',
						}}
					>
						No sites added yet...
					</Typography>
				)}
				{load && [1].map(() => <Skeleton variant="rectangular" height="44px" key={nanoid()} />)}
				{!load && !!emDataFull?.sites?.length && (
					<Box
						sx={{
							mt: 1,
							padding: '5px 5px',
							display: 'flex',
							flexDirection: 'row',
							gap: 0,
							alignItems: 'start',
							minHeight: '44px',
							justifyContent: 'space-between',
						}}
						// onMouseEnter={() => {
						// 	setMouseOver(true);
						// }}
						// onMouseLeave={() => {
						// 	setMouseOver(false);
						// }}
					>
						<Box
							sx={{
								display: 'flex',
								alignItems: 'center',
								gap: 1,
								flexWrap: 'wrap',
								// maxWidth: '900px',
							}}
						>
							{emDataFull?.sites?.map((site: TEmSites) => (
								<Box
									key={`${site.webAddress}-${site.integrationType}-${site.sitePlatform ?? 'none'}-${site.mainSite ? 'main' : 'alt'}`}
									sx={{
										display: 'flex',
										alignItems: 'center',
										gap: 0.5,
										lineHeight: '1',

										borderStyle: 'solid',
										borderWidth: site.mainSite ? '2px' : '1px',
										borderColor: site.mainSite ? COLORS.PRIMARY : COLORS.SECONDARY,
										borderRadius: '12px',
										padding: '1px 4px',
										justifyContent: 'center',
										textAlign: 'center',
										// backgroundColor: site.mainSite ? '#01619320' : 'transparent',
									}}
								>
									{site.sitePlatform &&
									Object.values(EEmSitePlatform).includes(site.sitePlatform) &&
									site.sitePlatform !== EEmSitePlatform.OTHER ? (
										<EasTooltip title={site.sitePlatform}>
											<img
												src={EmSitePlatformImages[site.sitePlatform]}
												alt={site.sitePlatform ?? ''}
												width={30}
												height={14}
												style={
													{
														// backgroundColor: COLORS.SECONDARY,
														// borderRadius: '12px',
														// padding: '1px 2px',
													}
												}
											/>
										</EasTooltip>
									) : site.sitePlatform === null ? (
										<EasTooltip title="No Platform">
											<Help color="primary" />
										</EasTooltip>
									) : (
										<EasTooltip title={site.sitePlatform}>
											<ShoppingBag color="primary" />
										</EasTooltip>
									)}
									{site.webAddress ? (
										<Typography
											variant="body1"
											color="primary"
											component="a"
											href={
												site.webAddress.startsWith('http://') ||
												site.webAddress.startsWith('https://')
													? site.webAddress
													: `https://${site.webAddress}`
											}
											target="_blank"
											rel="noopener noreferrer"
											sx={{
												fontSize: '0.9rem',
												fontWeight: 'bold',
												color: 'primary.main',
												textDecoration: 'none',
												cursor: 'pointer',
											}}
										>
											{site.webAddress}
										</Typography>
									) : (
										<Typography
											variant="body1"
											color="primary"
											component="span"
											sx={{
												fontSize: '0.9rem',
												fontWeight: 'bold',
												color: 'primary.main',
												textDecoration: 'none',
												cursor: 'default',
											}}
										>
											N/A
										</Typography>
									)}{' '}
									{site.sitePlatform && site.integrationType ? (
										<Typography
											variant="body2"
											color="primary"
											component="span"
											fontFamily="monospace"
											fontWeight="bold"
											sx={{
												fontSize: '0.7rem',
												backgroundColor: COLORS.BACKGROUND_SECONDARY,
												color: COLORS.font_status,
												padding: '0px 2px',
												borderRadius: '12px',
											}}
										>
											{site.integrationType.toLowerCase()}
										</Typography>
									) : (
										<Typography
											variant="body2"
											component="span"
											fontFamily="monospace"
											fontWeight="bold"
											sx={{
												fontSize: '0.7rem',
												backgroundColor: 'warning.light',
												color: 'warning.dark',
												padding: '0px 8px',
												borderRadius: '12px',
											}}
										>
											?
										</Typography>
									)}
									<IconButton
										size="small"
										color="primary"
										onClick={() => {
											navigator.clipboard
												.writeText(site.webAddress)
												.then(() => {
													setIsCopied(true);
													setCopyUrl(site.webAddress);
													setTimeout(() => {
														setIsCopied(false);
														setCopyUrl(null);
													}, 1000);
												})
												.catch(() => {
													setIsCopied(false);
												});
										}}
									>
										{isCopied && copyUrl === site.webAddress ? (
											<CircularProgress size={12} />
										) : (
											<ContentCopy sx={{ fontSize: '0.9rem', p: 0, m: 0 }} />
										)}
									</IconButton>
								</Box>
							))}
						</Box>
					</Box>
				)}
			</CardContent>
			<EmSitesManageDialogComponent
				open={dialogOpen}
				onClose={() => setDialogOpen(false)}
				emId={emDataFull?.id ?? 0}
				initialMode={dialogMode}
			/>
		</Card>
	);
};
